// Role-based Terms of Service content
// This file contains the terms of service text customized for different user roles
import { normalizeRole } from "../config/roles";

export const getTermsContent = (userRole) => {
  const isStudent = normalizeRole(userRole) === 'student';
  const isSocialWorker = normalizeRole(userRole) === 'socialworker';
  const isFreemium = isStudent || isSocialWorker;

  // Common sections that apply to all roles
  const commonSections = {
    acceptance: {
      title: "1. Acceptance of Terms",
      content: isFreemium
        ? "By creating an account, accessing, or using Magic Notes, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms apply to all users of the Service."
        : "By creating an account, accessing, or using Magic Notes, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. These Terms apply to all users of the Service, including without limitation users who are healthcare providers, medical professionals, patients, or any other individuals who access or use the Service."
    },
    accountResponsibilities: {
      title: "3. User Accounts and Responsibilities",
      accountCreation: "To use certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.",
      accountSecurity: "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security.",
      userConduct: "You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:",
      conductItems: [
        "Use the Service in any way that violates any applicable federal, state, local, or international law or regulation",
        "Transmit any material that is defamatory, offensive, or otherwise objectionable",
        "Attempt to gain unauthorized access to any portion of the Service or any other accounts, computer systems, or networks",
        "Interfere with or disrupt the Service or servers or networks connected to the Service",
        "Use any automated system, including \"robots,\" \"spiders,\" or \"offline readers,\" to access the Service",
        "Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Service without our express written permission"
      ]
    },
    intellectualProperty: {
      title: "5. Intellectual Property Rights",
      serviceContent: "The Service and its original content, features, and functionality are owned by VitaMed AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.",
      userContent: "You retain ownership of any content, data, or information you submit, upload, or transmit through the Service (\"User Content\"). By submitting User Content, you grant VitaMed AI a worldwide, non-exclusive, royalty-free, perpetual, irrevocable, and fully sublicensable license to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, and display such User Content for the purpose of providing and improving the Service.",
      feedback: "If you provide us with any feedback, suggestions, or ideas about the Service, you agree that we can use them without any obligation to compensate you."
    },
    limitationOfLiability: {
      title: "7. Limitation of Liability",
      content: [
        "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL VITAMED AI, ITS AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THE SERVICE.",
        "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, VITAMED AI ASSUMES NO LIABILITY OR RESPONSIBILITY FOR ANY (I) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT; (II) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE WHATSOEVER, RESULTING FROM YOUR ACCESS TO OR USE OF OUR SERVICE; (III) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL INFORMATION STORED THEREIN; (IV) ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM OUR SERVICE; (V) ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE, WHICH MAY BE TRANSMITTED TO OR THROUGH OUR SERVICE BY ANY THIRD PARTY."
      ]
    },
    serviceAvailability: {
      title: "8. Service Availability and Modifications",
      availability: "We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) with reasonable advance notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.",
      updates: "We may release updates, patches, or new versions of the Service from time to time. You may be required to update your software to continue using the Service."
    },
    termination: {
      title: "9. Termination",
      content: [
        "We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us at support@magicnotes.ai.",
        "All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability."
      ]
    },
    changesToTerms: {
      title: "10. Changes to Terms",
      content: [
        "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.",
        "By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service."
      ]
    },
    governingLaw: {
      title: "11. Governing Law and Dispute Resolution",
      content: "These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which VitaMed AI operates, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except where prohibited by law."
    },
    contactInfo: {
      title: "12. Contact Information",
      content: "If you have any questions about these Terms of Service, please contact us at:",
      details: {
        email: "support@magicnotes.ai",
        company: "VitaMed AI"
      }
    }
  };

  // Role-specific service descriptions
  const getServiceDescription = () => {
    if (isStudent) {
      return {
        title: "2. Description of Service",
        intro: "Magic Notes is a class note-taking and academic record management application that enables students to:",
        features: [
          "Record and transcribe class lectures and study sessions",
          "Generate organized class notes and study materials",
          "Store and manage class schedules and academic information",
          "Export notes for integration with study tools and learning management systems",
          "Track academic progress and deadlines"
        ],
        disclaimer: "The Service uses artificial intelligence and machine learning technologies to process audio recordings and generate notes. The accuracy of transcriptions and generated notes may vary and should be reviewed by the student. Students are responsible for verifying all transcribed content for accuracy before using it for academic purposes."
      };
    } else if (isSocialWorker) {
      return {
        title: "2. Description of Service",
        intro: "Magic Notes is a meeting management and case note-taking application that enables social workers to:",
        features: [
          "Record and transcribe client meetings and case conferences",
          "Generate professional meeting notes and case documentation",
          "Store and manage client meeting schedules and case information",
          "Export meeting notes for integration with case management systems",
          "Track client progress and follow-up tasks"
        ],
        disclaimer: "The Service uses artificial intelligence and machine learning technologies to process audio recordings and generate meeting notes. The accuracy of transcriptions and generated notes may vary and should be reviewed by qualified professionals. All documentation generated by the Service should be reviewed and verified before being included in official case files."
      };
    } else {
      // Default clinical/doctor version
      return {
        title: "2. Description of Service",
        intro: "Magic Notes is a clinical note-taking application that enables users to:",
        features: [
          "Record and transcribe clinical visit audio recordings",
          "Generate professional medical notes and SOAP notes",
          "Store and manage patient information and clinical documentation",
          "Export medical notes for integration with Electronic Health Record (EHR) systems",
          "Conduct analysis on clinical data"
        ],
        disclaimer: "The Service uses artificial intelligence and machine learning technologies to process audio recordings and generate clinical documentation. The accuracy of transcriptions and generated notes may vary and should be reviewed by qualified healthcare professionals."
      };
    }
  };

  // Role-specific data privacy sections
  const getDataPrivacySection = () => {
    if (isStudent) {
      return {
        title: "4. Student Data and Privacy",
        sections: [
          {
            subtitle: "4.1 Educational Records:",
            content: "Magic Notes may process, store, and transmit educational records and personal information. By using the Service, you acknowledge that you are responsible for ensuring compliance with all applicable student privacy laws, including FERPA (Family Educational Rights and Privacy Act) where applicable."
          },
          {
            subtitle: "4.2 Data Security:",
            content: "We implement reasonable administrative, physical, and technical safeguards to protect your data. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security."
          },
          {
            subtitle: "4.3 Class Recording Consent:",
            content: "You are solely responsible for obtaining all necessary permissions and consents before recording class lectures or study group sessions. You must comply with all institutional policies regarding class recording and sharing of educational content."
          },
          {
            subtitle: "4.4 Academic Integrity:",
            content: "You agree to use the Service in compliance with your educational institution's academic integrity policies. You are responsible for ensuring that your use of recorded lectures and AI-generated notes complies with your school's honor code and academic policies."
          }
        ]
      };
    } else if (isSocialWorker) {
      return {
        title: "4. Client Data and Privacy",
        sections: [
          {
            subtitle: "4.1 Confidential Client Information:",
            content: "Magic Notes may process, store, and transmit confidential client information. By using the Service, you acknowledge that you are responsible for ensuring compliance with all applicable privacy and confidentiality laws, including social work ethical standards and client confidentiality requirements."
          },
          {
            subtitle: "4.2 Data Security:",
            content: "We implement reasonable administrative, physical, and technical safeguards to protect client information. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security."
          },
          {
            subtitle: "4.3 Client Consent:",
            content: "You are solely responsible for obtaining all necessary consents and authorizations from clients before recording, transcribing, or storing their information using the Service. You must comply with all professional ethical standards and organizational policies regarding client confidentiality."
          },
          {
            subtitle: "4.4 Professional Standards:",
            content: "You agree to use the Service in accordance with professional social work ethics and standards, including those established by the National Association of Social Workers (NASW) or equivalent professional bodies."
          }
        ]
      };
    } else {
      // Default clinical/HIPAA version
      return {
        title: "4. Healthcare Data and HIPAA Compliance",
        sections: [
          {
            subtitle: "4.1 Protected Health Information (PHI):",
            content: "Magic Notes may process, store, and transmit Protected Health Information (PHI) as defined under the Health Insurance Portability and Accountability Act (HIPAA). By using the Service, you acknowledge that you are responsible for ensuring compliance with all applicable healthcare privacy and security laws, including HIPAA."
          },
          {
            subtitle: "4.2 Business Associate Agreement:",
            content: "If you are a Covered Entity or Business Associate under HIPAA, you may need to enter into a Business Associate Agreement (BAA) with VitaMed AI. Please contact us at support@magicnotes.ai to discuss BAA requirements."
          },
          {
            subtitle: "4.3 Data Security:",
            content: "We implement reasonable administrative, physical, and technical safeguards to protect PHI. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security."
          },
          {
            subtitle: "4.4 Patient Consent:",
            content: "You are solely responsible for obtaining all necessary consents and authorizations from patients before recording, transcribing, or storing their health information using the Service."
          }
        ]
      };
    }
  };

  // Role-specific disclaimer sections
  const getDisclaimerSection = () => {
    if (isStudent) {
      return {
        title: "6. Academic Tool Disclaimer",
        sections: [
          {
            subtitle: "6.1 Not a Substitute for Learning:",
            content: "Magic Notes is a note-taking and study tool and does not replace active learning, class participation, or independent study. The Service is not intended to substitute for attending classes or engaging with course materials directly."
          },
          {
            subtitle: "6.2 Accuracy Disclaimer:",
            content: "While we strive for accuracy, transcriptions and generated notes may contain errors. All notes generated by the Service should be reviewed, verified, and supplemented by students before use for studying, assignments, or examinations."
          },
          {
            subtitle: "6.3 No Warranty for Academic Use:",
            content: "You acknowledge that the Service is provided \"as is\" and that VitaMed AI makes no warranties, express or implied, regarding the accuracy, completeness, or suitability of any transcriptions or generated notes for academic purposes. We are not responsible for any academic consequences resulting from errors or inaccuracies in the Service."
          }
        ]
      };
    } else if (isSocialWorker) {
      return {
        title: "6. Professional Tool Disclaimer",
        sections: [
          {
            subtitle: "6.1 Not a Substitute for Professional Judgment:",
            content: "Magic Notes is a documentation tool and does not replace professional social work judgment, assessment, or intervention. The Service is not intended to substitute for professional evaluation or direct client interaction."
          },
          {
            subtitle: "6.2 Accuracy Disclaimer:",
            content: "While we strive for accuracy, transcriptions and generated notes may contain errors. All documentation generated by the Service should be reviewed, verified, and edited by qualified social work professionals before inclusion in official case files or client records."
          },
          {
            subtitle: "6.3 No Warranty for Professional Use:",
            content: "You acknowledge that the Service is provided \"as is\" and that VitaMed AI makes no warranties, express or implied, regarding the accuracy, completeness, or suitability of any transcriptions or generated notes for professional social work purposes. We are not responsible for any professional or legal consequences resulting from errors or inaccuracies in the Service."
          }
        ]
      };
    } else {
      // Default clinical version
      return {
        title: "6. Medical Disclaimer",
        sections: [
          {
            subtitle: "6.1 Not a Medical Device:",
            content: "Magic Notes is a documentation tool and does not provide medical advice, diagnosis, or treatment. The Service is not intended to be used as a medical device or to replace professional medical judgment."
          },
          {
            subtitle: "6.2 Accuracy Disclaimer:",
            content: "While we strive for accuracy, transcriptions and generated notes may contain errors. All clinical documentation generated by the Service should be reviewed, verified, and edited by qualified healthcare professionals before use in patient care or medical records."
          },
          {
            subtitle: "6.3 No Warranty for Medical Use:",
            content: "You acknowledge that the Service is provided \"as is\" and that VitaMed AI makes no warranties, express or implied, regarding the accuracy, completeness, or suitability of any transcriptions or generated notes for medical or clinical purposes."
          }
        ]
      };
    }
  };

  // Return the complete terms content structure
  return {
    intro: {
      welcome: isStudent
        ? "Welcome to Magic Notes, a class note-taking and academic management application developed by VitaMed AI. By accessing or using our Service, you agree to be bound by these Terms of Service (\"Terms\"). If you disagree with any part of these Terms, you may not access the Service."
        : isSocialWorker
        ? "Welcome to Magic Notes, a meeting management and case documentation application developed by VitaMed AI. By accessing or using our Service, you agree to be bound by these Terms of Service (\"Terms\"). If you disagree with any part of these Terms, you may not access the Service."
        : "Welcome to Magic Notes (also referred to as \"Magic Clinical Notes\" or \"the Service\"), a clinical note-taking application developed by VitaMed AI. By accessing or using our Service, you agree to be bound by these Terms of Service (\"Terms\"). If you disagree with any part of these Terms, you may not access the Service."
    },
    sections: [
      commonSections.acceptance,
      getServiceDescription(),
      commonSections.accountResponsibilities,
      getDataPrivacySection(),
      commonSections.intellectualProperty,
      getDisclaimerSection(),
      commonSections.limitationOfLiability,
      commonSections.serviceAvailability,
      commonSections.termination,
      commonSections.changesToTerms,
      commonSections.governingLaw,
      commonSections.contactInfo
    ]
  };
};

// Get short summary for modal
export const getTermsSummary = (userRole) => {
  const isStudent = normalizeRole(userRole) === 'student';
  const isSocialWorker = normalizeRole(userRole) === 'socialworker';

  if (isStudent) {
    return {
      welcome: "Welcome to Magic Notes! Please review and accept our Terms of Service to continue.",
      title: "By using Magic Notes, you agree to:",
      items: [
        "Use the service only for lawful educational purposes",
        "Maintain the confidentiality of your account",
        "Comply with your school's academic integrity policies",
        "Obtain necessary permissions before recording classes",
        "Review all generated notes for accuracy before use"
      ]
    };
  } else if (isSocialWorker) {
    return {
      welcome: "Welcome to Magic Notes! Please review and accept our Terms of Service to continue.",
      title: "By using Magic Notes, you agree to:",
      items: [
        "Use the service only for lawful professional purposes",
        "Maintain the confidentiality of your account",
        "Comply with professional social work ethics and standards",
        "Obtain necessary client consents before recording meetings",
        "Review all generated case notes for accuracy"
      ]
    };
  } else {
    return {
      welcome: "Welcome to Magic Notes! Please review and accept our Terms of Service to continue.",
      title: "By using Magic Notes, you agree to:",
      items: [
        "Use the service only for lawful purposes",
        "Maintain the confidentiality of your account",
        "Comply with all applicable healthcare privacy laws (HIPAA)",
        "Review all generated clinical notes for accuracy",
        "Obtain necessary patient consents before recording"
      ]
    };
  }
};
